import { Injectable, inject } from '@angular/core';
import { Map } from 'maplibre-gl';
import { WidgetService } from '../platform/widget.service';

/** Map tint strength used when the widget has `color_*` parameters but no `maptint`. */
export const DEFAULT_MAP_TINT = 25;

export interface Rgba { r: number; g: number; b: number; a: number; }

/**
 * Basemap layers that are "ground" (land, parks, buildings) lean toward the panel background;
 * everything else (roads, water, labels) leans toward the accent color.
 */
const GROUND_LAYER = /^(background|park|landcover|landuse|building|aeroway_fill|road_area)/;

let parseCanvas: CanvasRenderingContext2D | null | undefined;

/**
 * Parses any CSS color string (hex, rgb[a], hsl[a], named). Hex and rgb are parsed directly;
 * the rest goes through a 2D canvas, which normalizes whatever the browser accepts.
 */
export function parseColor(value: string): Rgba | null {
  const v = value.trim().toLowerCase();
  const hex = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/.exec(v);
  if (hex) {
    let h = hex[1];
    if (h.length <= 4) h = h.split('').map((c) => c + c).join('');
    return {
      r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16),
      a: h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1,
    };
  }
  const rgb = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+%?))?\s*\)$/.exec(v);
  if (rgb) {
    const alpha = rgb[4] == null ? 1 : rgb[4].endsWith('%') ? parseFloat(rgb[4]) / 100 : parseFloat(rgb[4]);
    return { r: +rgb[1], g: +rgb[2], b: +rgb[3], a: alpha };
  }
  if (!/^(hsla?\(|[a-z]+$)/.test(v)) return null;
  if (parseCanvas === undefined) {
    try {
      parseCanvas = document.createElement('canvas').getContext('2d');
    } catch {
      parseCanvas = null;
    }
  }
  if (!parseCanvas) return null;
  // Two sentinels: an unparseable value leaves fillStyle unchanged, so it reads back as both.
  parseCanvas.fillStyle = '#000001';
  parseCanvas.fillStyle = v;
  const first = parseCanvas.fillStyle;
  parseCanvas.fillStyle = '#000002';
  parseCanvas.fillStyle = v;
  if (first !== parseCanvas.fillStyle) return null;
  // Read back as #rrggbb or rgba(…), both handled above.
  return parseColor(String(first));
}

export function toCssColor(c: Rgba): string {
  const r = Math.round(c.r), g = Math.round(c.g), b = Math.round(c.b);
  return c.a >= 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${Math.round(c.a * 1000) / 1000})`;
}

export function mixColors(from: Rgba, to: Rgba, amount: number): Rgba {
  return {
    r: from.r + (to.r - from.r) * amount,
    g: from.g + (to.g - from.g) * amount,
    b: from.b + (to.b - from.b) * amount,
    a: from.a + (to.a - from.a) * amount,
  };
}

function rgbToHsl({ r, g, b }: Rgba): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h / 6, s, l];
}

function hslToRgb(h: number, s: number, l: number, a: number): Rgba {
  if (s === 0) return { r: l * 255, g: l * 255, b: l * 255, a };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return { r: channel(h + 1 / 3) * 255, g: channel(h) * 255, b: channel(h - 1 / 3) * 255, a };
}

/**
 * Moves `color` toward the hue and saturation of `target` by `amount` (0..1) while keeping
 * its own lightness and alpha: a light map stays light, white stays white, dark stays dark —
 * only the color cast shifts toward the palette.
 */
export function tintColor(color: Rgba, target: Rgba, amount: number): Rgba {
  const [th, ts] = rgbToHsl(target);
  const [, , l] = rgbToHsl(color);
  return mixColors(color, hslToRgb(th, ts, l, color.a), amount);
}

/** Applies `fn` to every color string inside a paint value (plain, legacy stops, or expression). */
function mapPaintColors(value: unknown, fn: (c: Rgba) => Rgba): unknown {
  if (typeof value === 'string') {
    const parsed = parseColor(value);
    return parsed ? toCssColor(fn(parsed)) : value;
  }
  if (Array.isArray(value)) {
    // Expressions start with their operator name ('interpolate', 'match', …) — never a color.
    return value.map((item, i) => (i === 0 && typeof item === 'string') ? item : mapPaintColors(item, fn));
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, mapPaintColors(v, fn)]));
  }
  return value;
}

/**
 * Widget mode: lets the map follow the embedding page's `color_*` palette.
 *
 * - The basemap (the evlight / evdark style) is tinted on every style load: each paint color
 *   keeps its lightness and moves `maptint` % toward the palette hue — ground layers toward
 *   `--base-background-color`, roads, water and labels toward `--base-gradient-color-1`.
 * - Layers the app draws itself (range, route, planning animation, turn arrows, cursor,
 *   charger pins) ask this service for their brand colors and get the accent color when
 *   `color_accent1` / `color_accent2` are given. Semantic colors (traffic, charger power
 *   tiers) are never touched.
 *
 * Outside the widget, or with `maptint=0`, every method returns its fallback unchanged.
 */
@Injectable({ providedIn: 'root' })
export class MapPaletteService {

  private readonly widgetService = inject(WidgetService);

  /** Tint strength, 0..1. 0 disables the whole service. */
  public readonly strength: number;
  private readonly accentGiven: boolean;
  private readonly surfaceGiven: boolean;

  private readonly tokenCache = new globalThis.Map<string, Rgba | null>();
  private readonly hookedMaps = new WeakSet<Map>();

  constructor() {
    const w = this.widgetService;
    const tint = w.getMapTint();
    this.strength = !w.isWidget() ? 0 : (tint ?? (w.getColors().length > 0 ? DEFAULT_MAP_TINT : 0)) / 100;
    this.accentGiven = this.strength > 0 && (w.hasColor('color_accent1') || w.hasColor('color_accent2'));
    this.surfaceGiven = this.strength > 0 && w.hasColor('color_lighttext');
  }

  public get active(): boolean {
    return this.strength > 0;
  }

  // ---------------------------------------------------------------------------
  // App layer colors
  // ---------------------------------------------------------------------------

  /** Brand color (range, route free flow, turn arrows): `color_accent1` when given. */
  public accent(fallback: string): string {
    return this.accentGiven ? this.css('--base-gradient-color-1', fallback) : fallback;
  }

  /** `accent` lightened toward white by `amount` (0..1) — the softer brand tones. */
  public accentLight(fallback: string, amount: number): string {
    const accent = this.accentGiven ? this.token('--base-gradient-color-1') : null;
    return accent ? toCssColor(mixColors(accent, { r: 255, g: 255, b: 255, a: 1 }, amount)) : fallback;
  }

  /** `accent` with the given alpha — glows and halos. */
  public accentAlpha(fallback: string, alpha: number): string {
    const accent = this.accentGiven ? this.token('--base-gradient-color-1') : null;
    return accent ? toCssColor({ ...accent, a: alpha }) : fallback;
  }

  /** Light fill of pins, cursor disc and turn arrows: `color_lighttext` when given. */
  public surface(fallback: string): string {
    return this.surfaceGiven ? this.css('--base-white-color', fallback) : fallback;
  }

  // ---------------------------------------------------------------------------
  // Basemap
  // ---------------------------------------------------------------------------

  /**
   * Tints the basemap after every style load: the first one, a skin switch (diffed) and the
   * full reloads. MapLibre fires `styledata` right before `style.load` in the same tick, so
   * calling this from the first `styleData` event still catches the initial load — before
   * anything is painted.
   */
  public hook(map: Map): void {
    if (!this.active || this.hookedMaps.has(map)) return;
    this.hookedMaps.add(map);
    map.on('style.load', () => this.tintBasemap(map));
  }

  private tintBasemap(map: Map): void {
    // The stylesheet is the style JSON as loaded; the app's own layers are not in it. Tinting
    // from these original values also keeps the result independent of earlier tints.
    const layers = map.style?.stylesheet?.layers ?? [];
    const ground = this.token('--base-background-color');
    const accent = this.token('--base-gradient-color-1');
    layers.forEach((layer) => {
      const target = GROUND_LAYER.test(layer.id) ? ground : accent;
      if (!target || !('paint' in layer) || !layer.paint || !map.getLayer(layer.id)) return;
      Object.entries(layer.paint).forEach(([property, value]) => {
        if (!property.endsWith('-color')) return;
        const tinted = mapPaintColors(value, (c) => tintColor(c, target, this.strength));
        try {
          // Same shape as the original value, colors swapped — the typings cannot follow that.
          (map.setPaintProperty as (id: string, name: string, value: unknown, options?: { validate?: boolean }) => Map)
            .call(map, layer.id, property, tinted, { validate: false });
        } catch { }
      });
    });
  }

  // ---------------------------------------------------------------------------

  /** Resolved value of a colors.scss token on the body (includes the `color_*` overrides). */
  private token(name: string): Rgba | null {
    try {
      return parseColor(getComputedStyle(document.body).getPropertyValue(name));
    } catch {
      return null;
    }
  }

  /**
   * Token as a CSS string, cached — only used for tokens set by a `color_*` parameter, which
   * are inline on the body and do not change with the skin. Route coloring calls this per point.
   */
  private css(name: string, fallback: string): string {
    if (!this.tokenCache.has(name)) this.tokenCache.set(name, this.token(name));
    const value = this.tokenCache.get(name);
    return value ? toCssColor(value) : fallback;
  }
}
